import React, { useCallback, createContext, useState, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { proposalService } from '../services/proposalService';

export const ProposalContext = createContext();

const getRandomId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export const ProposalProvider = props => {
    const navigate = useNavigate();
    const [proposalInput, setProposalInput] = useState([
        { id: getRandomId(), productId: -1, quantity: 1 }
    ]);
    const [openingEditorState, setOpeningEditorState] = useState(EditorState.createEmpty());
    const [termsAndConditionsEditorState, setTermsAndConditionsEditorState] = useState(EditorState.createEmpty());
    const [name, setName] = useState('');

    const [customerData, setCustomerData] = useState();
    const [proposals, setProposals] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortState, setSortState] = useState([{ fieldName: 'Timestamp', dir: 'desc' }]);
    const [acceptError, setAcceptError] = useState(null);
    const [proposalSaving, setProposalSaving] = useState(false);

    const addRow = useCallback(() => {
        setProposalInput([
            ...proposalInput,
            {
                id: getRandomId(),
                productId: -1,
                quantity: 1
            },
        ]);
    }, [proposalInput]);

    const removeRow = useCallback(
        num => {
            let temp = [...proposalInput];
            temp.splice(num, 1);
            if (temp.length === 0)
                temp = [{ id: getRandomId(), productId: -1, quantity: 1 }];

            setProposalInput(temp);
        },
        [proposalInput]
    );

    const updateRow = useCallback(
        (index, productId, quantity) => {
            const temp = [...proposalInput];
            temp[index].productId = productId;
            temp[index].quantity = quantity;
            setProposalInput(temp);
        },
        [proposalInput]
    );

    const getProposalById = useCallback(id => {
        return proposalService.getProposalById(id);
    }, []);

    const acceptProposal = useCallback(async viewToken => {
        try {
            return await proposalService.acceptProposal(viewToken);
        } catch (err) {
            setAcceptError(err);
        }
    }, []);

    const createProposal = useCallback(async () => {
        setProposalSaving(true);
        const opening = draftToHtml(convertToRaw(openingEditorState.getCurrentContent()));
        const termsAndConditions = draftToHtml(convertToRaw(termsAndConditionsEditorState.getCurrentContent()));
        const proposalItems = proposalInput.filter(x => x.productId !== -1);
        const { data: proposal } = await proposalService.postProposal(customerData.id, proposalItems, opening, termsAndConditions, name);
        navigate(`/proposals/edit/${proposal.id}`);
        setProposalSaving(false);
    }, [navigate, customerData, proposalInput, openingEditorState, termsAndConditionsEditorState, name]);

    const editProposal = useCallback(async proposalId => {
        setProposalSaving(true);
        const opening = draftToHtml(convertToRaw(openingEditorState.getCurrentContent()));
        const termsAndConditions = draftToHtml(convertToRaw(termsAndConditionsEditorState.getCurrentContent()));
        const proposalItems = proposalInput.filter(x => x.productId !== -1);
        await proposalService.editProposal(proposalId, proposalItems, opening, termsAndConditions, name);
        setProposalSaving(false);
    }, [proposalInput, openingEditorState, termsAndConditionsEditorState, name]);

    const deleteProposal = useCallback(async proposalId => {
        return proposalService.deleteProposal(proposalId);
    }, []);

    const getProposals = useCallback(async () => {
        const result = await proposalService.getProposals(pageSize, pageIndex, sortState[0]?.fieldName, sortState[0]?.dir);
        setProposals(result.data);
        setTotal(result.total);
    }, [pageSize, pageIndex, sortState]);

    const getProposalByViewToken = useCallback(viewToken => {
        return proposalService.getProposalByViewToken(viewToken);
    }, []);

    const sendProposal = useCallback(async id => {
        return await proposalService.sendProposalEmail(id);
    }, []);

    const updateEnvelopeStatus = useCallback(async (id, status) => {
        return await proposalService.updateEnvelopeStatus(id, status);
    }, []);

    const downloadProposal = useCallback(async (id) => {
        return await proposalService.downloadProposal(id);
    }, []);

    const values = useMemo(
        () => ({
            proposalInput,
            customerData,
            proposals,
            total,
            pageSize,
            pageIndex,
            openingEditorState,
            termsAndConditionsEditorState,
            acceptError,
            sortState,
            name,
            proposalSaving,
            addRow,
            removeRow,
            updateRow,
            setCustomerData,
            setProposalInput,
            createProposal,
            editProposal,
            deleteProposal,
            getProposals,
            setPageIndex,
            setPageSize,
            getProposalById,
            getProposalByViewToken,
            sendProposal,
            acceptProposal,
            updateEnvelopeStatus,
            setOpeningEditorState,
            setTermsAndConditionsEditorState,
            setAcceptError,
            setSortState,
            setName,
            downloadProposal
        }),
        [
            proposalInput,
            customerData,
            proposals,
            total,
            pageSize,
            pageIndex,
            openingEditorState,
            termsAndConditionsEditorState,
            acceptError,
            sortState,
            name,
            proposalSaving,
            addRow,
            removeRow,
            updateRow,
            setCustomerData,
            setProposalInput,
            createProposal,
            editProposal,
            deleteProposal,
            getProposals,
            setPageIndex,
            setPageSize,
            getProposalById,
            getProposalByViewToken,
            sendProposal,
            acceptProposal,
            updateEnvelopeStatus,
            setOpeningEditorState,
            setTermsAndConditionsEditorState,
            setAcceptError,
            setSortState,
            setName,
            downloadProposal
        ]
    );

    return <ProposalContext.Provider value={values}>{props.children}</ProposalContext.Provider>;
};

export const useProposals = () => {
    const context = useContext(ProposalContext);

    if (context === undefined) {
        throw new Error('useProposals must be used within a ProposalProvider component.');
    }

    return context;
};
